import React, { useEffect, lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';
import TagManager from 'react-gtm-module';
import posthog from 'posthog-js';

// Lazy load heavy pages/components
const SingleThreatDetail = lazy(
  () => import('./pages/ThreadsDetails/SingleThreatDetail'),
);
const TakedownList = lazy(() => import('./pages/TakedownList/TakedownList'));
const ActiveDefense = lazy(() => import('./pages/ActiveDefense/ActiveDefense'));
const FalsePositive = lazy(() => import('./pages/FalsePositive/FalsePositive'));
const EnrichTakedown = lazy(
  () => import('./pages/TakedownList/EnrichTakedown'),
);
const SingleTakedownDetail = lazy(
  () => import('./pages/TakedownList/SingleTakedownDetail'),
);
const GSBHistory = lazy(() => import('./pages/GSBHistory/GSBHistory'));
const Feeds = lazy(() => import('./pages/Feeds/Feeds'));
const CTIGraphPage = lazy(() => import('./pages/CTIGraph/CTIGraphPage'));
const CompanyDetailsV2 = lazy(
  () => import('./pages/CompanyDetailsV2/CompanyDetailsV2'),
);
const CompaniesListV2 = lazy(
  () => import('./pages/CompaniesListV2/CompaniesListV2'),
);
const CompanyCreationV2 = lazy(
  () => import('./pages/CompanyCreationV2/CompanyCreationV2'),
);
const HistoricalScans = lazy(
  () => import('./pages/HistoricalScan/HistoricalScan'),
);
const RegistrarDetails = lazy(
  () => import('./pages/settings/registrars-table/RegistrarDetails'),
);
const Registrars = lazy(
  () => import('./pages/settings/registrars-table/Registrars'),
);
const Nameservers = lazy(
  () => import('./pages/settings/nameservers/Nameservers'),
);
const TLDs = lazy(() => import('./pages/settings/tlds/TLDs'));
const FNDiscovery = lazy(() => import('./pages/FNDiscovery/FNDiscovery'));
const BulkActions = lazy(() => import('./pages/BulkActions/BulkActions'));
const DomainLookup = lazy(() => import('./pages/DomainLookup/DomainLookup'));
const ThreatExplainStats = lazy(
  () => import('./pages/ThreatExplainStats/ThreatExplainStats'),
);

// Import routing components and layout normally (not lazy) - they're used in every route
import { QueryClientProvider } from '@tanstack/react-query';
import Home from './pages/Home/Home';
import Login from './pages/Login/Login';
import PublicRoute from './routes/PublicRoute';
import PrivateRoute from './routes/PrivateRoute';
import AuthRedirectHandler from './routes/AuthRedirectHandler';
import DefaultLayout from './components/Layout/Layout';
import CommandPalette from './components/CommandPalette/CommandPalette';
import AiSummaryFlusher from './components/AiSummaryFlusher/AiSummaryFlusher';
import BulkTakedownNotifier from './components/BulkTakedownNotifier/BulkTakedownNotifier';
import useApiInterceptor from './helpers/useApiInterceptor';
import { setAuthTokenProvider } from './services/authTokenProvider';
import { Loader } from './components';

import {
  REACT_APP_ENVIRONMENT,
  REACT_APP_GTM_ID,
  REACT_APP_SENTRY_DSN,
  REACT_APP_POSTHOG_KEY,
  REACT_APP_POSTHOG_HOST,
} from './utils/envVar';
import { SingleThreatProvider } from './context/SingleThreat/SingleThreatContext';
import { WebSocketProvider } from './context/WebSocket/WebSocketContext';
import { queryClient } from './queries/queryClient';
import { GsbProvider } from './context/GsbHistory/GsbHistoryContext';
import { ErrorBoundary, FallbackComponent } from './helpers/ErrorBoundary';
import packageJson from '../package.json';
import { useAuth0 } from './__mocks__/@auth0/auth0-react';

Sentry.init({
  dsn: REACT_APP_SENTRY_DSN,
  environment: REACT_APP_ENVIRONMENT,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      networkDetailAllowUrls: ['api.bfore.ai'],
    }),
  ],
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 1.0 : 0.1,
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/([a-zA-Z0-9-]+\.)*bfore\.ai\//,
  ],
  replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 0,
  replaysOnErrorSampleRate: process.env.NODE_ENV === 'production' ? 1.0 : 0,
  release: packageJson.version ?? 'unknown-release',
});

const tagManagerArgs: any = {
  gtmId: REACT_APP_GTM_ID,
};

TagManager.initialize(tagManagerArgs);

if (REACT_APP_POSTHOG_KEY) {
  posthog.init(REACT_APP_POSTHOG_KEY, {
    api_host: REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
  });
}

function App() {
  useApiInterceptor();
  const { isAuthenticated, getIdTokenClaims, getAccessTokenSilently } =
    useAuth0();

  useEffect(() => {
    setAuthTokenProvider(async () => {
      // getAccessTokenSilently is served from the SDK cache while tokens are
      // valid and renews them (refresh token) when expired — the ID token the
      // backend expects is renewed alongside and read from the claims.
      await getAccessTokenSilently();
      const claims: any = await getIdTokenClaims();
      const idToken = claims?.__raw ?? null;
      if (idToken) {
        window.localStorage.setItem('userToken', idToken);
      }
      return idToken;
    });
    return () => setAuthTokenProvider(null);
  }, [getAccessTokenSilently, getIdTokenClaims]);

  useEffect(() => {
    const connect = async () => {
      const token: any = await getIdTokenClaims();
      const user = {
        email: token?.email,
        name: token?.name,
      };
      Sentry.setUser(user);
      if (token?.email) {
        posthog.identify(token.email, {
          email: token.email,
          name: token.name,
        });
      }
      (window as any)?.dataLayer?.push({
        event: 'appInit',
        userName: user?.name,
        userEmail: user?.email,
      });
    };
    if (isAuthenticated) {
      connect();
    }
  }, [isAuthenticated, getIdTokenClaims]);

  const PageLoader = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <Loader size={50} />
    </div>
  );

  const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
  return (
    <Sentry.ErrorBoundary fallback={<FallbackComponent />} showDialog>
      <QueryClientProvider client={queryClient}>
        <WebSocketProvider>
          <Router>
            <AuthRedirectHandler />
            <AiSummaryFlusher />
            <BulkTakedownNotifier />
            <CommandPalette />
            <SentryRoutes>
              <Route path="/" element={<PublicRoute element={<Login />} />} />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Home />
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/threats/customer-review-requests"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Home />
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="threats/:protectedId/:threatId/:companyId"
                element={
                  <PrivateRoute
                    element={
                      <SingleThreatProvider>
                        <GsbProvider>
                          <ErrorBoundary>
                            <Suspense fallback={<PageLoader />}>
                              <SingleThreatDetail />
                            </Suspense>
                          </ErrorBoundary>
                        </GsbProvider>
                      </SingleThreatProvider>
                    }
                  />
                }
              />
              <Route
                path="/takedown"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <Outlet />
                      </DefaultLayout>
                    }
                  />
                }
              >
                <Route
                  index
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <TakedownList />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path=":takedown_id"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <SingleTakedownDetail />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="enrichment"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <EnrichTakedown />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
              </Route>
              <Route
                path="/companies"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <CompaniesListV2 />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />

              <Route
                path="/companies/new"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <CompanyCreationV2 />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/companies/:id/view"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <CompanyDetailsV2 />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/network-disruption"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <ActiveDefense />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/false-positive"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <FalsePositive />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/historical-scan"
                element={
                  <PrivateRoute
                    element={
                      <SingleThreatProvider>
                        <GsbProvider>
                          <DefaultLayout>
                            <ErrorBoundary>
                              <Suspense fallback={<PageLoader />}>
                                <HistoricalScans />
                              </Suspense>
                            </ErrorBoundary>
                          </DefaultLayout>
                        </GsbProvider>
                      </SingleThreatProvider>
                    }
                  />
                }
              />
              <Route
                path="/gsb"
                element={
                  <PrivateRoute
                    element={
                      <SingleThreatProvider>
                        <GsbProvider>
                          <DefaultLayout>
                            <ErrorBoundary>
                              <Suspense fallback={<PageLoader />}>
                                <GSBHistory />
                              </Suspense>
                            </ErrorBoundary>
                          </DefaultLayout>
                        </GsbProvider>
                      </SingleThreatProvider>
                    }
                  />
                }
              />
              <Route
                path="/feeds"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Feeds />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/cti-graph"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <CTIGraphPage />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/domain-lookup"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <DomainLookup />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/bulk-actions"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <BulkActions />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/threat-explain-stats"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <ThreatExplainStats />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/fn-discovery"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <FNDiscovery />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Registrars />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/settings/registrars"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Registrars />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/settings/registrars-table"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Registrars />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/settings/registrars/:id"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <RegistrarDetails />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/settings/nameservers"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Nameservers />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
              <Route
                path="/settings/tlds"
                element={
                  <PrivateRoute
                    element={
                      <DefaultLayout>
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <TLDs />
                          </Suspense>
                        </ErrorBoundary>
                      </DefaultLayout>
                    }
                  />
                }
              />
            </SentryRoutes>
          </Router>
        </WebSocketProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
